import { useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
  ChakraBox,
  ChakraFlex,
  FAIconName,
  FireflyButton,
  Icon,
  Text,
} from '@equip.health/ui';

import { Path } from '~/routes/constants';
import { PatientTabs } from '~/lib/constants/patients.constants';
import { PatientTabsCollection } from '~/lib/hooks/usePatientTabsCollection';
import { CarePlanningTab } from '~/components/maud/patients/profile/tabs/careplan/CarePlanningSubTabHeader';
import {
  PatientProviderDashboard,
  VIEW_DETAIL_TYPE,
} from '~/store/services/providerDashboard';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { ProviderDashboardEvents } from '~/analytics/providerDashboardEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import {
  formatAuthEndDate,
  formatDischargeSessionDate,
  getDischargePlanLabel,
  getDischargeRelativeLabel,
} from '../provider-dashboard.util';
import PatientIdentity from './PatientIdentity';

interface DischargePatientRowProps {
  patient: PatientProviderDashboard;
}

const DischargePatientRow = ({ patient }: DischargePatientRowProps) => {
  const history = useHistory();
  const [isHovered, setIsHovered] = useState(false);
  const { trackEvent } = useAnalytics() ?? {};

  const { patientExternalId, viewDetail } = patient;
  const discharge =
    viewDetail?.type === VIEW_DETAIL_TYPE.DISCHARGE ? viewDetail : null;

  const openDischargePlan = () => {
    history.push(
      `${Path.MAUD_PATIENTS}/${patientExternalId}/${
        PatientTabsCollection[PatientTabs.CARE_PLAN].subPath
      }/${CarePlanningTab.DISCHARGE_PLAN}`,
    );
  };

  if (!discharge) return null;

  const planLabel = getDischargePlanLabel(discharge.dischargePlanStatus);
  const authEnd = discharge.insuranceAuthEndDate
    ? formatAuthEndDate(discharge.insuranceAuthEndDate)
    : null;

  const footer = (
    <Text
      variant="caption"
      data-testid={`discharge-plan-detail-${patientExternalId}`}
      color="neutral.secondary"
      marginTop="4px"
    >
      {planLabel}
      {authEnd ? ` · Auth end ${authEnd}` : ''}
    </Text>
  );

  return (
    <ChakraFlex
      alignItems="center"
      borderTop="1px solid"
      borderColor="neutral.line"
      cursor="pointer"
      data-testid={`discharge-row-${patientExternalId}`}
      gap="12px"
      padding="14px 0"
      onClick={() => {
        trackEvent?.(ProviderDashboardEvents.myPatientsDischargeRowClicked, {
          dischargePlanStatus: discharge.dischargePlanStatus,
          Source: AnalyticsSourceValues.PROVIDER_DASHBOARD,
        });
        openDischargePlan();
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      _hover={{ bg: 'neutral.background.secondary' }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          trackEvent?.(ProviderDashboardEvents.myPatientsDischargeRowClicked, {
            dischargePlanStatus: discharge.dischargePlanStatus,
            Source: AnalyticsSourceValues.PROVIDER_DASHBOARD,
          });
          openDischargePlan();
        }
      }}
    >
      <PatientIdentity patient={patient} footer={footer} />

      <ChakraBox flexShrink={0}>
        {isHovered ? (
          <FireflyButton
            id={`discharge-open-${patientExternalId}`}
            size="small"
            leftIcon={FAIconName.clipboardListRegular}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              trackEvent?.(ProviderDashboardEvents.myPatientsOpenDischargePlanClicked, {
                dischargePlanStatus: discharge.dischargePlanStatus,
                Source: AnalyticsSourceValues.PROVIDER_DASHBOARD,
              });
              openDischargePlan();
            }}
          >
            Open discharge plan
          </FireflyButton>
        ) : (
          <ChakraFlex
            data-testid={`discharge-session-date-${patientExternalId}`}
            alignItems="center"
            gap="6px"
            border="1px solid"
            borderColor="neutral.line"
            borderRadius="6px"
            padding="4px 8px"
            bg="neutral.background.primary"
            whiteSpace="nowrap"
          >
            <Icon
              name={FAIconName.calendarRegular}
              color="neutral.secondary"
              size="sm"
            />
            <Text variant="caption" color="neutral.primary">
              {formatDischargeSessionDate(discharge.dischargeSessionDate)}{' '}
              {getDischargeRelativeLabel(discharge.dischargeSessionDate)}
            </Text>
          </ChakraFlex>
        )}
      </ChakraBox>
    </ChakraFlex>
  );
};

export default DischargePatientRow;
